import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import XIcon from "@/assets/icons/x.svg?react";
import { SubmitHandler, useForm } from "react-hook-form";
import StyledSpinner from "@/components/styled/StyledSpinner";
import { useUpdateGlobalParameters } from "@/api/globalParameters.api";
import {
  GlobalParametersDefaultPaymentTermFormData,
  GlobalParametersDefaultPaymentTermSchema,
} from "@/types/forms/bo/globalParameters.form";
import { ControlledInput } from "@/components/ui/customs/controlled-input";

type OptionsPaymentTermModalProps = {
  closeModal: () => void;
  defaultPaymentTerm: number;
  title: string;
};

export const OptionsPaymentTermModal = ({
  closeModal,
  defaultPaymentTerm,
  title,
}: OptionsPaymentTermModalProps) => {
  const { t } = useTranslation("boParameters");
  const { mutateAsync: updateGlobalParameters, isPending } = useUpdateGlobalParameters();

  const defaultPaymentTermForm = useForm<GlobalParametersDefaultPaymentTermFormData>({
    resolver: zodResolver(GlobalParametersDefaultPaymentTermSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: {
      defaultPaymentTerm: defaultPaymentTerm,
    },
  });

  const onSubmit: SubmitHandler<GlobalParametersDefaultPaymentTermFormData> = async (data) => {
    const ret = await updateGlobalParameters(data);
    if (ret?.status === 200) {
      closeModal();
    }
  };

  return (
    <dialog className="fixed top-0 left-0 z-50 flex h-full w-full items-center justify-center overflow-auto bg-[#00000080] backdrop-blur-sm">
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[480px] rounded-lg bg-white"
      >
        <div className="flex w-full flex-row items-center justify-between border-b border-gray-200 p-6">
          <div />
          <h2 className="text-center text-2xl font-semibold text-gray-900">{title}</h2>
          <button
            onClick={closeModal}
            className="h-fit w-fit rounded-full font-semibold hover:bg-gray-200"
          >
            <XIcon />
          </button>
        </div>
        <form
          onSubmit={defaultPaymentTermForm.handleSubmit(onSubmit)}
          className="flex w-full flex-col gap-4 p-6"
        >
          <ControlledInput
            control={defaultPaymentTermForm.control}
            name="defaultPaymentTerm"
            label={t("paymentTermModal.termLabel")}
            defaultValue={defaultPaymentTerm}
            inputType="number"
          />
          <button
            type="submit"
            disabled={isPending}
            className="bg-brand700 hover:bg-brand600 h-fit w-full rounded-lg px-4 py-3 text-base font-semibold text-white"
          >
            {isPending ? <StyledSpinner /> : t("paymentTermModal.submit")}
          </button>
        </form>
      </div>
    </dialog>
  );
};
